"use client";

import { useState, useEffect, useMemo } from "react";
import useGooglePlacesService from "react-google-autocomplete/lib/usePlacesAutocompleteService";

import type { RestrictToAreaOptions } from "./AddressAutocomplete.types";
import type { RestrictedValidationOptions } from "./AddressAutocomplete.types";
import { transformCenterWithRadiusToLatLngBounds } from "./AddressAutocomplete.utils";

export function cleanString(str: string) {
    return str.trim().toLowerCase().replace(/ /g, "").replace(/,/g, "");
}

interface IValidateAddressOptions {
    defaultAddress?: string;
}

/**
 * Because of fallback to new array in this line https://github.com/ErrorPro/react-google-autocomplete/blob/abe3fa34c4f9130abc7496de3daa1c04e3c60156/src/usePlacesAutocompleteService.js#L107
 * useGooglePlacesService can return new array each call, making things rerender endlessly,
 * f.e. in AddressAutocomplete useEffect for onPredictionsChanged
 */
const useMemoPredictions = (placePredictions: google.maps.places.AutocompletePrediction[]) => {
    return useMemo(() => placePredictions, [JSON.stringify(placePredictions)]);
};

export const useValidateAddress = (options: IValidateAddressOptions = {}) => {
    const {
        placePredictions: predictionsFromHook,
        getPlacePredictions,
        autocompleteSessionToken,
        isPlacePredictionsLoading,
    } = useGooglePlacesService({
        libraries: ["places", "geometry"],
        sessionToken: true,
        debounce: 500,
        language: "en",
    });
    const placePredictions = useMemoPredictions(predictionsFromHook);
    const defaultAddress = options.defaultAddress ?? "";
    const [currentAddress, setCurrentAddress] = useState(defaultAddress);
    const isCurrentAddressDefault = currentAddress === defaultAddress;
    function isValid() {
        /**
         * If the value of the address hasn't changed from the default one,
         * then it is implied that the address is valid (was previously allowed to
         * be selected). Without this, on the first render, the `placePredictions`
         * will be empty and any address will be considered invalid.
         * */
        if (isCurrentAddressDefault) {
            return true;
        }

        // Some places descriptions are not in english and special characters do not allow simple comparison between strings
        const predictedPlace = placePredictions.find(
            (place) =>
                cleanString(place.description) === cleanString(currentAddress) || place.matched_substrings?.length >= 3,
        );

        if (!predictedPlace) {
            return false;
        }

        return currentAddress !== "";
    }

    const validateAddress = (address: string, restricted: RestrictedValidationOptions = {}) => {
        let origin: google.maps.LatLng | undefined;
        let bounds: google.maps.LatLngBounds | undefined;

        setCurrentAddress(address);

        if (restricted.area) {
            origin = new google.maps.LatLng(restricted.area.center.latitude, restricted.area.center.longitude);
            bounds = transformCenterWithRadiusToLatLngBounds(origin, restricted.area.radiusInMeters);
        }

        getPlacePredictions({
            input: address,
            sessionToken: autocompleteSessionToken,
            componentRestrictions: {
                country: restricted.countries || null,
            },
            locationBias: bounds,
            origin,
            types: [],
        });
    };

    return {
        validateAddress,
        isValid: isPlacePredictionsLoading ? true : isValid() && !!currentAddress,
    };
};

// The `useGooglePlacesService from "react-google-autocomplete/lib/usePlacesAutocompleteService"` has an unfortunate bug.
// It is likely that there is some kind of race condition inside the library. If we initialise the hook "too fast" `placesService` gets returned as `null` before it's filled in,
// and subsequent rerender of the hook is needed from our side (hook doesn't rerender on its own...).
// Thus we emloy this "hacky" fix.
const useHandlePlacesServiceInitializationBug = (placesService: google.maps.places.PlacesService | null) => {
    const [attemptsToFetchPlacesService, setAttemptsToFetchPlacesService] = useState(0);

    useEffect(() => {
        if (!placesService && attemptsToFetchPlacesService <= 3) {
            setTimeout(() => {
                setAttemptsToFetchPlacesService((val) => val + 1);
            }, 100);
        }
    }, [placesService, attemptsToFetchPlacesService]);
};

export const usePlacesService = ({
    restrictedArea,
    restrictedCountries,
    placePredictionsTypes = ["street_address", "point_of_interest", "locality", "premise"],
}: {
    restrictedArea?: RestrictToAreaOptions;
    restrictedCountries?: string | string[];
    placePredictionsTypes?: string[];
}) => {
    const {
        placesService,
        placePredictions: predictionsFromHook,
        getPlacePredictions,
        isPlacePredictionsLoading,
        autocompleteSessionToken,
        getQueryPredictions,
    } = useGooglePlacesService({
        sessionToken: true,
        debounce: 500,
        language: "en",
    });

    const placePredictions = useMemoPredictions(predictionsFromHook);

    useHandlePlacesServiceInitializationBug(placesService);

    const getPlacePredictionsForValue = (value: string) => {
        let origin: google.maps.LatLng | undefined;
        let bounds: google.maps.LatLngBounds | undefined;

        if (restrictedArea) {
            origin = new google.maps.LatLng(restrictedArea.center.latitude, restrictedArea.center.longitude);
            bounds = transformCenterWithRadiusToLatLngBounds(origin, restrictedArea.radiusInMeters);
        }

        return getPlacePredictions({
            input: value,
            sessionToken: autocompleteSessionToken,
            componentRestrictions: {
                country: restrictedCountries || null,
            },
            locationBias: bounds,
            origin,
            types: placePredictionsTypes,
        });
    };

    return {
        getPlacePredictions: getPlacePredictionsForValue,
        placesService,
        placePredictions,
        isPlacePredictionsLoading,
        autocompleteSessionToken,
        getQueryPredictions,
    };
};
